import { COMPONENT_TOKENS } from '../generator/componentTokens';
import { isComponentSizeKey } from '../generator/emitCss';
import { buildRoleTokens } from '../generator/roleTokens';
import { defaultSeeds } from '../seeds/defaultSeeds';
import type { TokenMap } from '../generator/types';

export const RADIUS_TOKEN_DEFAULTS: TokenMap = {
  'radius-sm': '0.25em',
  'radius-md': '0.5em',
  'radius-lg': '0.75em',
  'radius-xl': '1em',
  'radius-full': '9999px',
};

export const TYPE_TOKEN_DEFAULTS: TokenMap = {
  font: "'Basier Circle', system-ui, sans-serif",
  'font-mono': "ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace",
  'font-size-xs': '0.75rem',
  'font-size-sm': '0.875rem',
  'font-size-md': '1rem',
  'font-size-lg': '1.125rem',
  'font-size-xl': '1.25rem',
  'font-size-2xl': '1.5rem',
};


export const SIZE_TOKEN_DEFAULTS: TokenMap = Object.fromEntries(
  Object.entries(COMPONENT_TOKENS).filter(([key]) => isComponentSizeKey(key)),
);

export const EDITABLE_TOKEN_GROUP_IDS = [
  'surface',
  'border',
  'text',
  'input',
  'brand',
  'neutral',
  'danger',
  'success',
  'warning',
  'info',
  'focus',
  'nav',
  'table',
  'link',
  'type',
  'size',
  'radius',
] as const;

export type EditableTokenGroupId = (typeof EDITABLE_TOKEN_GROUP_IDS)[number];
export type EditableTokenKind = 'color' | 'radius' | 'font' | 'size';

export type EditableTokenDef = {
  key: string;
  kind: EditableTokenKind;
  group: EditableTokenGroupId;
  modeSensitive: boolean;
};

const INTENT_GROUPS = ['brand', 'neutral', 'danger', 'success', 'warning', 'info'] as const;

export function classifyEditableToken(key: string): EditableTokenGroupId | null {
  if (key.startsWith('radius-')) return 'radius';
  if (key === 'font' || key === 'font-mono' || key.startsWith('font-size-')) return 'type';
  if (isComponentSizeKey(key)) return 'size';
  if (key.startsWith('surface-') || key === 'bg-app' || key === 'overlay-bg') return 'surface';
  if (key.startsWith('border')) return 'border';
  if (key.startsWith('fg') || key.startsWith('disabled-')) return 'text';
  if (key.startsWith('input-')) return 'input';
  for (const role of INTENT_GROUPS) {
    if (key.startsWith(`${role}-`)) return role;
  }
  if (key.startsWith('focus-')) return 'focus';
  if (key.startsWith('nav-')) return 'nav';
  if (key.startsWith('table-')) return 'table';
  if (key.startsWith('link')) return 'link';
  return null;
}

function buildCatalog(): EditableTokenDef[] {
  const roleKeys = Object.keys(buildRoleTokens(defaultSeeds)).sort();
  const role: EditableTokenDef[] = roleKeys.map((key) => {
    const group = classifyEditableToken(key);
    if (!group || group === 'radius') {
      throw new Error(`nx-tokens: unclassified role token ${key}`);
    }
    return { key, kind: 'color', group, modeSensitive: true };
  });
  const radius: EditableTokenDef[] = Object.keys(RADIUS_TOKEN_DEFAULTS).map((key) => ({
    key,
    kind: 'radius',
    group: 'radius',
    modeSensitive: false,
  }));
  const type: EditableTokenDef[] = Object.keys(TYPE_TOKEN_DEFAULTS).map((key) => ({
    key,
    kind: key.startsWith('font-size-') ? 'size' : 'font',
    group: 'type',
    modeSensitive: false,
  }));
  const size: EditableTokenDef[] = Object.keys(SIZE_TOKEN_DEFAULTS).map((key) => ({
    key,
    kind: 'size',
    group: 'size',
    modeSensitive: false,
  }));
  const rank = new Map(EDITABLE_TOKEN_GROUP_IDS.map((id, i) => [id, i]));
  return [...role, ...type, ...size, ...radius].sort((a, b) => {
    const g = (rank.get(a.group) ?? 99) - (rank.get(b.group) ?? 99);
    return g !== 0 ? g : a.key.localeCompare(b.key);
  });
}

export const EDITABLE_TOKEN_CATALOG: readonly EditableTokenDef[] = buildCatalog();

export const EDITABLE_TOKEN_BY_KEY: ReadonlyMap<string, EditableTokenDef> = new Map(
  EDITABLE_TOKEN_CATALOG.map((token) => [token.key, token]),
);

export function isEditableTokenKey(key: string): boolean {
  return EDITABLE_TOKEN_BY_KEY.has(key);
}
